<script setup>
/* eslint-disable vue/no-unused-properties */
// currentPrompt prop is provided for future integration
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AgentsAPI from 'dashboard/api/agents/agents';

defineProps({
  currentPrompt: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['apply']);

const { t } = useI18n();

// Form fields
const businessName = ref('');
const sector = ref('');
const tone = ref('professional');
const role = ref('');
const goals = ref('');
const restrictions = ref('');
const additionalInfo = ref('');

// Generated prompt
const generatedPrompt = ref('');
const isGenerating = ref(false);

// Dialog ref
const dialogRef = ref(null);

const toneOptions = [
  {
    value: 'professional',
    label: t('AI_AGENTS.PROMPT_GENERATOR.TONES.PROFESSIONAL'),
  },
  {
    value: 'friendly',
    label: t('AI_AGENTS.PROMPT_GENERATOR.TONES.FRIENDLY'),
  },
  { value: 'formal', label: t('AI_AGENTS.PROMPT_GENERATOR.TONES.FORMAL') },
  { value: 'casual', label: t('AI_AGENTS.PROMPT_GENERATOR.TONES.CASUAL') },
  {
    value: 'empathetic',
    label: t('AI_AGENTS.PROMPT_GENERATOR.TONES.EMPATHETIC'),
  },
  {
    value: 'technical',
    label: t('AI_AGENTS.PROMPT_GENERATOR.TONES.TECHNICAL'),
  },
];

const canGenerate = computed(() => {
  return businessName.value.trim() && role.value.trim();
});

const open = () => {
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const resetForm = () => {
  businessName.value = '';
  sector.value = '';
  tone.value = 'professional';
  role.value = '';
  goals.value = '';
  restrictions.value = '';
  additionalInfo.value = '';
  generatedPrompt.value = '';
};

const handleGenerate = async () => {
  if (!canGenerate.value) return;

  isGenerating.value = true;

  try {
    const response = await AgentsAPI.generatePrompt({
      business_name: businessName.value,
      sector: sector.value,
      tone: tone.value,
      role: role.value,
      goals: goals.value,
      restrictions: restrictions.value,
      additional_info: additionalInfo.value,
    });

    generatedPrompt.value = response.data.prompt;
  } catch (error) {
    useAlert(error?.message || t('AI_AGENTS.PROMPT_GENERATOR.ERROR'));
  } finally {
    isGenerating.value = false;
  }
};

const handleApply = () => {
  if (generatedPrompt.value) {
    emit('apply', generatedPrompt.value);
    close();
    resetForm();
  }
};

const handleCopy = async () => {
  if (generatedPrompt.value) {
    try {
      await navigator.clipboard.writeText(generatedPrompt.value);
      useAlert(t('AI_AGENTS.PROMPT_GENERATOR.COPIED'));
    } catch {
      useAlert(t('AI_AGENTS.PROMPT_GENERATOR.COPY_ERROR'));
    }
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('AI_AGENTS.PROMPT_GENERATOR.TITLE')"
    width="3xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="resetForm"
  >
    <div class="space-y-6 max-h-[70vh] overflow-y-auto px-0.5 -mx-0.5">
      <!-- Description -->
      <p class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.PROMPT_GENERATOR.DESCRIPTION') }}
      </p>

      <!-- Form Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Business Name -->
        <Input
          v-model="businessName"
          :label="t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.BUSINESS_NAME')"
          :placeholder="
            t('AI_AGENTS.PROMPT_GENERATOR.PLACEHOLDERS.BUSINESS_NAME')
          "
          required
        />

        <!-- Sector -->
        <Input
          v-model="sector"
          :label="t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.SECTOR')"
          :placeholder="t('AI_AGENTS.PROMPT_GENERATOR.PLACEHOLDERS.SECTOR')"
        />

        <!-- Role -->
        <Input
          v-model="role"
          :label="t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.ROLE')"
          :placeholder="t('AI_AGENTS.PROMPT_GENERATOR.PLACEHOLDERS.ROLE')"
          required
        />

        <!-- Tone -->
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
            {{ t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.TONE') }}
          </label>
          <select
            v-model="tone"
            class="w-full px-3 py-2 bg-n-alpha-2 border border-n-weak rounded-lg text-sm text-n-slate-12 focus:border-n-brand focus:ring-1 focus:ring-n-brand transition-colors"
          >
            <option
              v-for="option in toneOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Goals -->
      <TextArea
        v-model="goals"
        :label="t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.GOALS')"
        :placeholder="t('AI_AGENTS.PROMPT_GENERATOR.PLACEHOLDERS.GOALS')"
        :rows="3"
      />

      <!-- Restrictions -->
      <TextArea
        v-model="restrictions"
        :label="t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.RESTRICTIONS')"
        :placeholder="t('AI_AGENTS.PROMPT_GENERATOR.PLACEHOLDERS.RESTRICTIONS')"
        :rows="3"
      />

      <!-- Additional Info -->
      <TextArea
        v-model="additionalInfo"
        :label="t('AI_AGENTS.PROMPT_GENERATOR.FIELDS.ADDITIONAL_INFO')"
        :placeholder="
          t('AI_AGENTS.PROMPT_GENERATOR.PLACEHOLDERS.ADDITIONAL_INFO')
        "
        :rows="2"
      />

      <!-- Generate Button -->
      <div class="flex justify-center">
        <Button
          :label="t('AI_AGENTS.PROMPT_GENERATOR.GENERATE')"
          icon="i-lucide-sparkles"
          :is-loading="isGenerating"
          :disabled="!canGenerate"
          @click="handleGenerate"
        />
      </div>

      <!-- Generated Prompt -->
      <div v-if="generatedPrompt" class="mt-6">
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('AI_AGENTS.PROMPT_GENERATOR.GENERATED_PROMPT') }}
          </label>
          <button
            type="button"
            class="text-xs text-n-brand hover:text-n-brand-dark transition-colors"
            @click="handleCopy"
          >
            {{ t('AI_AGENTS.PROMPT_GENERATOR.COPY') }}
          </button>
        </div>
        <div
          class="p-4 bg-n-solid-1 border border-n-weak rounded-xl font-mono text-[13px] leading-relaxed text-n-slate-12 whitespace-pre-wrap max-h-72 overflow-y-auto"
        >
          {{ generatedPrompt }}
        </div>

        <!-- Apply Button -->
        <div class="flex justify-end gap-3 mt-4">
          <Button
            :label="t('AI_AGENTS.PROMPT_GENERATOR.REGENERATE')"
            variant="faded"
            color="slate"
            icon="i-lucide-refresh-cw"
            :is-loading="isGenerating"
            @click="handleGenerate"
          />
          <Button
            :label="t('AI_AGENTS.PROMPT_GENERATOR.APPLY')"
            icon="i-lucide-check"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
